import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { DssUserRoles, FlightZoneErrorType, FlightZoneListFilters, RestrictionsListItem } from "@dtm-frontend/dss-shared-lib";
import { AuthState } from "@dtm-frontend/shared/auth";
import { KmlExporterService } from "@dtm-frontend/shared/map";
import { PAGE_NUMBER_QUERY_PARAM, PAGE_SIZE_QUERY_PARAM } from "@dtm-frontend/shared/ui";
import { RxjsUtils } from "@dtm-frontend/shared/utils";
import { TranslocoService } from "@jsverse/transloco";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngxs/store";
import { ToastrService } from "ngx-toastr";
import { distinctUntilChanged, switchMap, tap } from "rxjs";
import { map } from "rxjs/operators";
import { RestrictionListRouteData, RestrictionsTableColumns } from "../../models/flight-zone-management.models";
import { FlightZoneListsActions } from "../../state/flight-zone-lists.actions";
import { FlightZoneListsState } from "../../state/flight-zone-lists.state";

@UntilDestroy()
@Component({
    selector: "dss-admin-lib-restrictions-list-container",
    templateUrl: "./restrictions-list-container.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestrictionsListContainerComponent implements OnInit {
    protected readonly displayedColumns: RestrictionsTableColumns = [
        "expandHandle",
        "notam",
        "zoneNumber",
        "startAt",
        "endAt",
        "applicationPurpose",
        "title",
        "actions",
    ];
    protected readonly initialFilters: FlightZoneListFilters;
    protected readonly isProcessing$ = this.store.select(FlightZoneListsState.isProcessing);
    protected readonly restrictionsList$ = this.store.select(this.routeData.selector);
    protected readonly hasListDataRetrievalError$ = this.store.select(FlightZoneListsState.hasListDataRetrievalError);
    protected readonly listFiltersCapabilities$ = this.store.select(FlightZoneListsState.listFiltersCapabilities);
    protected readonly institutionsFilterOptions$ = this.store.select(FlightZoneListsState.institutionsFilterOptions);
    protected readonly hasSupervisorPermissions$ = this.store
        .select(AuthState.roles)
        .pipe(map((roles) => roles?.includes(DssUserRoles.AnspSupervisor)));

    private get routeData(): RestrictionListRouteData {
        return this.route.snapshot.data as RestrictionListRouteData;
    }

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly toastService: ToastrService,
        private readonly transloco: TranslocoService,
        private readonly kmlExporterService: KmlExporterService
    ) {
        this.initialFilters = this.route.snapshot.queryParams;
    }

    public ngOnInit(): void {
        this.updateListOnQueryParamsChange();
        this.store.dispatch(new FlightZoneListsActions.GetInstitutionsFilterOptions());
    }

    protected applyFilters(filters: FlightZoneListFilters): void {
        this.updateQueryParams(filters);
    }

    protected findInstitutionByText(searchValue?: string): void {
        this.store.dispatch(new FlightZoneListsActions.GetInstitutionsFilterOptions(searchValue));
    }

    protected clearFilterLists(): void {
        this.store.dispatch(new FlightZoneListsActions.ClearFilterLists());
    }

    protected changePage(event: PageEvent): void {
        const queryParams = { [PAGE_NUMBER_QUERY_PARAM]: event.pageIndex, [PAGE_SIZE_QUERY_PARAM]: event.pageSize };

        this.updateQueryParams(queryParams);
    }

    protected refreshList(): void {
        this.store.dispatch(new this.routeData.action(this.route.snapshot.queryParams));
    }

    protected downloadKml(restriction: RestrictionsListItem): void {
        this.store
            .dispatch(new FlightZoneListsActions.GetRestrictionGeoJson(restriction))
            .pipe(
                tap(() => {
                    const error = this.store.selectSnapshot(FlightZoneListsState.error);

                    if (error?.type === FlightZoneErrorType.CannotGetZoneGeoJson) {
                        this.toastService.error(
                            this.transloco.translate("dssAdminLibFlightZoneManagement.applicationListShared.getZoneGeoJsonErrorMessage")
                        );
                    }
                }),
                switchMap(() => this.store.select(FlightZoneListsState.zoneGeoJson)),
                RxjsUtils.filterFalsy(),
                untilDestroyed(this)
            )
            .subscribe((geoJson) => {
                this.kmlExporterService.geoJsonToKml(geoJson, restriction.zoneNumber);
            });
    }

    private updateQueryParams(queryParams: Params): void {
        this.router.navigate(["."], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: "merge",
        });
    }

    private updateListOnQueryParamsChange(): void {
        this.route.queryParams.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((filterParams) => {
            this.store.dispatch(new this.routeData.action(filterParams));
        });
    }
}
